import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Plus, Trash2, Edit, Save, X, Trophy, Users, Calendar, 
  MapPin, ChevronDown, ChevronUp, GitBranch, Globe, ArrowLeft, Hash, Wand2, BarChart2, Copy
} from "lucide-react";
import { Button } from "./ui/button";
import { Header } from "./Header";
import { ASNLoader } from "./ASNLoader";
import axios from "axios";
import { extractDominantColor } from "../utils/colorExtractor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const BracketManagerPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [brackets, setBrackets] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [existingTeams, setExistingTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [expandedBracket, setExpandedBracket] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [newBracket, setNewBracket] = useState({
    name: "",
    bracket_type: "single",
    num_teams: 8,
    seeding_enabled: false
  });

  const [newTeam, setNewTeam] = useState({ name: "", abbreviation: "", logo_url: "", color: "#FFFFFF", website_url: "", seed: "" });
  const [editingTeam, setEditingTeam] = useState(null);
  const [editTeamData, setEditTeamData] = useState({ name: "", abbreviation: "", logo_url: "", color: "#FFFFFF", website_url: "", seed: "" });
  const [editingGame, setEditingGame] = useState(null);
  
  // Clone bracket state
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [cloneSources, setCloneSources] = useState([]);
  const [cloneLoading, setCloneLoading] = useState(false);
  const [cloneForm, setCloneForm] = useState({
    source_event_id: "",
    source_bracket_id: "",
    new_bracket_name: "",
    include_teams: true,
    include_game_dates: false
  });
  const [cloningBracket, setCloningBracket] = useState(false);

  const fetchData = async () => {
    try {
      const [eventRes, bracketsRes, broadcastsRes, teamsRes] = await Promise.all([
        axios.get(`${API}/events/${eventId}`),
        axios.get(`${API}/events/${eventId}/brackets`),
        axios.get(`${API}/broadcasts/upcoming?limit=100`),
        axios.get(`${API}/teams`).catch(() => ({ data: [] }))
      ]);
      setEvent(eventRes.data);
      setBrackets(bracketsRes.data || []);
      setBroadcasts(broadcastsRes.data || []);
      setExistingTeams(teamsRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [eventId]);

  const handleCreateBracket = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/events/${eventId}/brackets`, newBracket, { headers: getAuthHeader() });
      setShowCreateForm(false);
      setNewBracket({ name: "", bracket_type: "single", num_teams: 8, seeding_enabled: false });
      fetchData();
    } catch (error) {
      console.error("Error creating bracket:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBracket = async (bracketId) => {
    if (!window.confirm("Delete this bracket? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/events/${eventId}/brackets/${bracketId}`, { headers: getAuthHeader() });
      fetchData();
    } catch (error) {
      console.error("Error deleting bracket:", error);
    }
  };

  const handleAddTeam = async (bracketId) => {
    if (!newTeam.name.trim()) return;
    try {
      const teamData = { ...newTeam };
      if (teamData.seed === "") teamData.seed = null;
      else teamData.seed = parseInt(teamData.seed) || null;
      await axios.post(`${API}/events/${eventId}/brackets/${bracketId}/teams`, teamData, { headers: getAuthHeader() });
      setNewTeam({ name: "", abbreviation: "", logo_url: "", color: "#FFFFFF", website_url: "", seed: "" });
      fetchData();
    } catch (error) {
      console.error("Error adding team:", error);
    }
  };

  const handleEditTeam = (team) => {
    setEditingTeam(team.id);
    setEditTeamData({
      name: team.name || "",
      abbreviation: team.abbreviation || "",
      logo_url: team.logo_url || "",
      color: team.color || "#FFFFFF",
      website_url: team.website_url || "",
      seed: team.seed ?? ""
    });
  };

  const handleSaveTeam = async (bracketId, teamId) => {
    try {
      const teamData = { ...editTeamData };
      if (teamData.seed === "") teamData.seed = null;
      else teamData.seed = parseInt(teamData.seed) || null;
      await axios.put(`${API}/events/${eventId}/brackets/${bracketId}/teams/${teamId}`, teamData, { headers: getAuthHeader() });
      setEditingTeam(null);
      fetchData();
    } catch (error) {
      console.error("Error updating team:", error);
    }
  };

  const handleDeleteTeam = async (bracketId, teamId) => {
    try {
      await axios.delete(`${API}/events/${eventId}/brackets/${bracketId}/teams/${teamId}`, { headers: getAuthHeader() });
      fetchData();
    } catch (error) {
      console.error("Error deleting team:", error);
    }
  };

  const handleUpdateGame = async (bracketId, gameId, updates) => {
    try {
      await axios.put(`${API}/events/${eventId}/brackets/${bracketId}/games/${gameId}`, updates, { headers: getAuthHeader() });
      fetchData();
      setEditingGame(null);
    } catch (error) {
      console.error("Error updating game:", error);
    }
  };

  const handleAdvanceWinner = async (bracketId, gameId) => {
    try {
      await axios.post(`${API}/events/${eventId}/brackets/${bracketId}/games/${gameId}/advance`, {}, { headers: getAuthHeader() });
      fetchData();
    } catch (error) {
      console.error("Error advancing winner:", error);
    }
  };

  const handleUpdateRound = async (bracketId, roundNumber, title, location) => {
    try {
      await axios.put(`${API}/events/${eventId}/brackets/${bracketId}/rounds/${roundNumber}?title=${encodeURIComponent(title)}&location=${encodeURIComponent(location)}`, {}, { headers: getAuthHeader() });
      fetchData();
    } catch (error) {
      console.error("Error updating round:", error);
    }
  };

  const handleToggleSeeding = async (bracketId, enabled) => {
    try {
      await axios.put(`${API}/events/${eventId}/brackets/${bracketId}/seeding?enabled=${enabled}`, {}, { headers: getAuthHeader() });
      fetchData();
    } catch (error) {
      console.error("Error toggling seeding:", error);
    }
  };

  // Clone bracket functions
  const openCloneModal = async () => {
    setShowCloneModal(true);
    setCloneLoading(true);
    try {
      const res = await axios.get(`${API}/events/brackets/available-for-clone`, { headers: getAuthHeader() });
      // Filter out current event from sources
      const filtered = (res.data || []).filter(item => item.event.id !== eventId);
      setCloneSources(filtered);
    } catch (error) {
      console.error("Error fetching clone sources:", error);
    } finally {
      setCloneLoading(false);
    }
  };

  const handleCloneBracket = async () => {
    if (!cloneForm.source_event_id || !cloneForm.source_bracket_id) {
      alert("Please select a bracket to clone");
      return;
    }
    
    setCloningBracket(true);
    try {
      await axios.post(`${API}/events/${eventId}/brackets/clone`, cloneForm, { headers: getAuthHeader() });
      setShowCloneModal(false);
      setCloneForm({
        source_event_id: "",
        source_bracket_id: "",
        new_bracket_name: "",
        include_teams: true,
        include_game_dates: false
      });
      fetchData();
    } catch (error) {
      console.error("Error cloning bracket:", error);
      alert(error.response?.data?.detail || "Failed to clone bracket");
    } finally {
      setCloningBracket(false);
    }
  };

  const selectedSourceEvent = cloneSources.find(s => s.event.id === cloneForm.source_event_id);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Header />
        <div className="flex items-center justify-center py-32">
          <ASNLoader size="large" />
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-32 text-center">
          <Trophy className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
          <h1 className="font-secondary text-2xl font-bold text-white mb-2">Event Not Found</h1>
          <Link to="/admin">
            <Button className="bg-white text-black hover:bg-zinc-200 text-xs px-6 py-2 h-auto rounded-none">
              Back to Admin
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />
      
      <div className="pt-8 pb-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/admin')}
                className="p-2 text-zinc-500 hover:text-white hover:bg-white/10 rounded"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-4">
                {event.logo_url && (
                  <img src={event.logo_url} alt={event.name} className="w-12 h-12 object-contain" />
                )}
                <div>
                  <h1 className="font-secondary text-2xl font-bold uppercase text-white">{event.name}</h1>
                  <p className="text-sm text-zinc-500">Bracket Manager</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Link to={`/events/${event.slug}`} target="_blank">
                <Button className="bg-zinc-800 text-white hover:bg-zinc-700 text-xs px-4 py-2 h-auto rounded-none">
                  View Public Page
                </Button>
              </Link>
              <Button 
                onClick={openCloneModal}
                className="bg-purple-600 text-white hover:bg-purple-700 text-xs px-4 py-2 h-auto rounded-none"
                data-testid="clone-bracket-btn"
              >
                <Copy className="w-4 h-4 mr-2" /> Clone from Event
              </Button>
              <Button 
                onClick={() => setShowCreateForm(true)}
                className="bg-white text-black hover:bg-zinc-200 text-xs px-4 py-2 h-auto rounded-none"
              >
                <Plus className="w-4 h-4 mr-2" /> New Bracket
              </Button>
            </div>
          </div>

          {/* Clone Bracket Modal */}
          {showCloneModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-zinc-900 border border-white/10 p-6 w-full max-w-lg mx-4"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-secondary text-lg font-bold uppercase text-white">
                    <Copy className="w-5 h-5 inline mr-2" />
                    Clone Bracket from Previous Event
                  </h3>
                  <button onClick={() => setShowCloneModal(false)} className="text-zinc-500 hover:text-white">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {cloneLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                  </div>
                ) : cloneSources.length === 0 ? (
                  <div className="text-center py-8">
                    <GitBranch className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                    <p className="text-zinc-400">No brackets available to clone</p>
                    <p className="text-zinc-600 text-sm mt-1">Create brackets in other events first</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Source Event Selection */}
                    <div>
                      <label className="block text-xs text-zinc-500 uppercase mb-1">Source Event *</label>
                      <select
                        value={cloneForm.source_event_id}
                        onChange={(e) => setCloneForm({ ...cloneForm, source_event_id: e.target.value, source_bracket_id: "" })}
                        className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
                        data-testid="clone-source-event"
                      >
                        <option value="">Select an event...</option>
                        {cloneSources.map(source => (
                          <option key={source.event.id} value={source.event.id}>
                            {source.event.name} ({source.event.start_date})
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Source Bracket Selection */}
                    {selectedSourceEvent && (
                      <div>
                        <label className="block text-xs text-zinc-500 uppercase mb-1">Source Bracket *</label>
                        <select
                          value={cloneForm.source_bracket_id}
                          onChange={(e) => setCloneForm({ ...cloneForm, source_bracket_id: e.target.value })}
                          className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
                          data-testid="clone-source-bracket"
                        >
                          <option value="">Select a bracket...</option>
                          {selectedSourceEvent.brackets.map(bracket => (
                            <option key={bracket.id} value={bracket.id}>
                              {bracket.name} ({bracket.bracket_type} elim, {bracket.team_count} teams)
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* New Bracket Name */}
                    <div>
                      <label className="block text-xs text-zinc-500 uppercase mb-1">New Bracket Name (optional)</label>
                      <input
                        type="text"
                        value={cloneForm.new_bracket_name}
                        onChange={(e) => setCloneForm({ ...cloneForm, new_bracket_name: e.target.value })}
                        placeholder="Leave blank to use original name + (Clone)"
                        className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
                      />
                    </div>

                    {/* Clone Options */}
                    <div className="space-y-3 pt-2">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={cloneForm.include_teams}
                          onChange={(e) => setCloneForm({ ...cloneForm, include_teams: e.target.checked })}
                          className="w-4 h-4 bg-zinc-800 border-white/20 rounded"
                        />
                        <div>
                          <span className="text-sm text-white">Include Teams</span>
                          <p className="text-[10px] text-zinc-500">Copy team names, logos, and seeding</p>
                        </div>
                      </label>
                      
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={cloneForm.include_game_dates}
                          onChange={(e) => setCloneForm({ ...cloneForm, include_game_dates: e.target.checked })}
                          className="w-4 h-4 bg-zinc-800 border-white/20 rounded"
                        />
                        <div>
                          <span className="text-sm text-white">Include Game Dates/Times</span>
                          <p className="text-[10px] text-zinc-500">Copy scheduled dates (you can adjust later)</p>
                        </div>
                      </label>
                    </div>

                    {/* Info Box */}
                    <div className="bg-zinc-800/50 border border-white/5 p-3 text-xs text-zinc-400">
                      <strong className="text-white">Note:</strong> Scores, winners, and broadcast links will NOT be copied. 
                      Only the bracket structure and optionally teams/dates will be cloned.
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-3 pt-2">
                      <Button
                        onClick={handleCloneBracket}
                        disabled={cloningBracket || !cloneForm.source_bracket_id}
                        className="bg-purple-600 text-white hover:bg-purple-700 text-xs px-4 py-2 h-auto rounded-none disabled:opacity-50"
                        data-testid="clone-bracket-submit"
                      >
                        {cloningBracket ? "Cloning..." : "Clone Bracket"}
                      </Button>
                      <button
                        type="button"
                        onClick={() => setShowCloneModal(false)}
                        className="text-zinc-500 text-sm hover:text-white"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </motion.div>
            </div>
          )}

          {/* Create Bracket Form */}
          {showCreateForm && (
            <motion.form
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              onSubmit={handleCreateBracket}
              className="p-6 bg-zinc-900/50 border border-white/10 space-y-4 mb-8"
            >
              <h4 className="font-secondary text-sm font-bold uppercase text-zinc-400">Create New Bracket</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-zinc-500 uppercase mb-1">Bracket Name *</label>
                  <input
                    type="text"
                    value={newBracket.name}
                    onChange={(e) => setNewBracket({ ...newBracket, name: e.target.value })}
                    placeholder="e.g., Varsity Division"
                    required
                    className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 uppercase mb-1">Elimination Type</label>
                  <select
                    value={newBracket.bracket_type}
                    onChange={(e) => setNewBracket({ ...newBracket, bracket_type: e.target.value })}
                    className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
                  >
                    <option value="single">Single Elimination</option>
                    <option value="double">Double Elimination</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 uppercase mb-1">Number of Teams</label>
                  <input
                    type="number"
                    min="2"
                    max="64"
                    value={newBracket.num_teams}
                    onChange={(e) => setNewBracket({ ...newBracket, num_teams: parseInt(e.target.value) || 2 })}
                    className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
                  />
                  <p className="text-[10px] text-zinc-600 mt-1">Enter any number - byes will be auto-calculated</p>
                </div>
              </div>

              {/* Seeding Toggle */}
              <div className="flex items-center gap-3 pt-2">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newBracket.seeding_enabled}
                    onChange={(e) => setNewBracket({ ...newBracket, seeding_enabled: e.target.checked })}
                    className="sr-only peer"
                    data-testid="seeding-toggle-create"
                  />
                  <div className="w-9 h-5 bg-zinc-700 peer-checked:bg-green-600 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all"></div>
                </label>
                <div>
                  <span className="text-sm text-white font-medium">Enable Seeding</span>
                  <p className="text-[10px] text-zinc-500">Assign seed numbers (1, 2, 3...) to rank teams</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <Button type="submit" disabled={saving} className="bg-white text-black hover:bg-zinc-200 text-xs px-4 py-2 h-auto rounded-none">
                  {saving ? "Creating..." : "Create Bracket"}
                </Button>
                <button type="button" onClick={() => setShowCreateForm(false)} className="text-zinc-500 text-sm hover:text-white">
                  Cancel
                </button>
              </div>
            </motion.form>
          )}

          {/* Brackets List */}
          {brackets.length === 0 ? (
            <div className="text-center py-16 bg-zinc-900/50 border border-white/5">
              <GitBranch className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-500 text-lg">No brackets created yet</p>
              <p className="text-xs text-zinc-600 mt-2">Create a bracket to start organizing your tournament</p>
            </div>
          ) : (
            <div className="space-y-6">
              {brackets.map(bracket => (
                <BracketCard
                  key={bracket.id}
                  bracket={bracket}
                  broadcasts={broadcasts}
                  existingTeams={existingTeams}
                  eventId={eventId}
                  eventColor={event.color}
                  expanded={expandedBracket === bracket.id}
                  onToggle={() => setExpandedBracket(expandedBracket === bracket.id ? null : bracket.id)}
                  onDelete={() => handleDeleteBracket(bracket.id)}
                  onAddTeam={() => handleAddTeam(bracket.id)}
                  onEditTeam={handleEditTeam}
                  onSaveTeam={(teamId) => handleSaveTeam(bracket.id, teamId)}
                  onDeleteTeam={(teamId) => handleDeleteTeam(bracket.id, teamId)}
                  onUpdateGame={(gameId, updates) => handleUpdateGame(bracket.id, gameId, updates)}
                  onAdvanceWinner={(gameId) => handleAdvanceWinner(bracket.id, gameId)}
                  onUpdateRound={(roundNum, title, loc) => handleUpdateRound(bracket.id, roundNum, title, loc)}
                  onToggleSeeding={(enabled) => handleToggleSeeding(bracket.id, enabled)}
                  newTeam={newTeam}
                  setNewTeam={setNewTeam}
                  editingTeam={editingTeam}
                  setEditingTeam={setEditingTeam}
                  editTeamData={editTeamData}
                  setEditTeamData={setEditTeamData}
                  editingGame={editingGame}
                  setEditingGame={setEditingGame}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Bracket Card Component
const BracketCard = ({ 
  bracket, broadcasts, existingTeams, eventId, eventColor, expanded, onToggle, onDelete,
  onAddTeam, onEditTeam, onSaveTeam, onDeleteTeam, onUpdateGame, onAdvanceWinner, onUpdateRound,
  newTeam, setNewTeam, editingTeam, setEditingTeam, editTeamData, setEditTeamData,
  editingGame, setEditingGame, onToggleSeeding
}) => {
  const [activeTab, setActiveTab] = useState("teams");
  const [editingRound, setEditingRound] = useState(null);
  const [roundTitle, setRoundTitle] = useState("");
  const [roundLocation, setRoundLocation] = useState("");
  const [gameFormData, setGameFormData] = useState({});
  const [showExistingTeamDropdown, setShowExistingTeamDropdown] = useState(false);

  // Handle selecting an existing team from the dropdown
  const handleSelectExistingTeam = (team) => {
    setNewTeam({
      name: team.name,
      logo_url: team.logo_url || "",
      color: team.primary_color || "#FFFFFF",
      website_url: team.website_url || "",
      seed: ""
    });
    setShowExistingTeamDropdown(false);
  };

  // Auto-extract color from logo
  const [extractingColor, setExtractingColor] = useState(false);
  
  const handleExtractColor = async () => {
    if (!newTeam.logo_url) return;
    
    setExtractingColor(true);
    try {
      const color = await extractDominantColor(newTeam.logo_url);
      setNewTeam(prev => ({ ...prev, color }));
    } catch (error) {
      console.error("Failed to extract color:", error);
    } finally {
      setExtractingColor(false);
    }
  };

  const getTeamById = (teamId) => {
    if (!teamId) return null;
    return bracket.teams?.find(t => t.id === teamId);
  };

  const getGamesByRound = (roundNumber) => {
    return bracket.games?.filter(g => g.round_number === roundNumber) || [];
  };

  const winnersRounds = bracket.rounds?.filter(r => {
    const games = getGamesByRound(r.round_number);
    return games.length > 0 && !games[0]?.is_losers_bracket;
  }) || [];

  const losersRounds = bracket.rounds?.filter(r => {
    const games = getGamesByRound(r.round_number);
    return games.length > 0 && games[0]?.is_losers_bracket;
  }) || [];

  const getRoundGames = (roundNumber, isLosers = false) => {
    return bracket.games?.filter(g => 
      g.round_number === roundNumber && g.is_losers_bracket === isLosers
    ).sort((a, b) => a.position - b.position) || [];
  };

  const handleEditGame = (game) => {
    setEditingGame(game.id);
    setGameFormData({
      team1_id: game.team1_id || "",
      team2_id: game.team2_id || "",
      team1_score: game.team1_score ?? "",
      team2_score: game.team2_score ?? "",
      winner_id: game.winner_id || "",
      broadcast_id: game.broadcast_id || "",
      stats_link: game.stats_link || "",
      game_date: game.game_date || "",
      game_time: game.game_time || ""
    });
  };

  const handleSaveGame = (gameId) => {
    const updates = { ...gameFormData };
    if (updates.team1_score === "") updates.team1_score = null;
    if (updates.team2_score === "") updates.team2_score = null;
    onUpdateGame(gameId, updates);
  };

  return (
    <div className="bg-zinc-900/50 border border-white/10 overflow-hidden">
      {/* Bracket Header */}
      <div 
        className="flex items-center gap-4 p-4 cursor-pointer hover:bg-white/5"
        onClick={onToggle}
      >
        <div 
          className="w-12 h-12 flex items-center justify-center flex-shrink-0 rounded"
          style={{ backgroundColor: eventColor + '20' }}
        >
          <Trophy className="w-6 h-6" style={{ color: eventColor }} />
        </div>
        
        <div className="flex-1 min-w-0">
          <h4 className="font-secondary text-xl font-bold text-white">{bracket.name}</h4>
          <div className="flex items-center gap-4 text-sm text-zinc-500">
            <span>{bracket.bracket_type === "double" ? "Double Elimination" : "Single Elimination"}</span>
            <span>•</span>
            <span>{bracket.teams?.length || 0} teams</span>
            <span>•</span>
            <span>{bracket.num_byes} bye{bracket.num_byes !== 1 ? "s" : ""}</span>
            {bracket.seeding_enabled && (
              <>
                <span>•</span>
                <span className="flex items-center gap-1 text-yellow-500">
                  <Hash className="w-3 h-3" /> Seeded
                </span>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button 
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="p-2 text-zinc-500 hover:text-red-500"
          >
            <Trash2 className="w-5 h-5" />
          </button>
          {expanded ? <ChevronUp className="w-6 h-6 text-zinc-500" /> : <ChevronDown className="w-6 h-6 text-zinc-500" />}
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="border-t border-white/10">
          {/* Tabs */}
          <div className="flex border-b border-white/10 overflow-x-auto scrollbar-hide">
            <button
              onClick={() => setActiveTab("teams")}
              className={`px-6 py-3 text-sm font-bold uppercase ${activeTab === "teams" ? "bg-white/10 text-white" : "text-zinc-500 hover:text-white"}`}
            >
              <Users className="w-4 h-4 inline mr-2" />Teams ({bracket.teams?.length || 0})
            </button>
            <button
              onClick={() => setActiveTab("bracket")}
              className={`px-6 py-3 text-sm font-bold uppercase ${activeTab === "bracket" ? "bg-white/10 text-white" : "text-zinc-500 hover:text-white"}`}
            >
              <GitBranch className="w-4 h-4 inline mr-2" />Bracket View
            </button>
            <button
              onClick={() => setActiveTab("rounds")}
              className={`px-6 py-3 text-sm font-bold uppercase ${activeTab === "rounds" ? "bg-white/10 text-white" : "text-zinc-500 hover:text-white"}`}
            >
              <Calendar className="w-4 h-4 inline mr-2" />Rounds ({bracket.rounds?.length || 0})
            </button>
            
            {/* Seeding Toggle */}
            <div className="ml-auto flex items-center gap-3 px-4">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={bracket.seeding_enabled || false}
                  onChange={(e) => onToggleSeeding(e.target.checked)}
                  className="sr-only peer"
                  data-testid="seeding-toggle"
                />
                <div className="w-9 h-5 bg-zinc-700 peer-checked:bg-yellow-600 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all"></div>
              </label>
              <span className="text-xs text-zinc-400 flex items-center gap-1">
                <Hash className="w-3 h-3" /> Seeding
              </span>
            </div>
          </div>

          {/* Teams Tab */}
          {activeTab === "teams" && (
            <div className="p-6 space-y-6">
              {/* Select from Existing Teams */}
              {existingTeams && existingTeams.length > 0 && (
                <div className="p-4 bg-zinc-800/30 border border-zinc-700 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-bold text-white flex items-center gap-2">
                        <Users className="w-4 h-4 text-orange-500" />
                        Select from Team Manager
                      </h4>
                      <p className="text-xs text-zinc-500 mt-1">Choose from {existingTeams.length} existing teams or add manually below</p>
                    </div>
                    <button
                      onClick={() => setShowExistingTeamDropdown(!showExistingTeamDropdown)}
                      className="px-4 py-2 bg-orange-500 text-black text-xs font-bold hover:bg-orange-400 flex items-center gap-2"
                    >
                      <Plus className="w-3 h-3" /> Select Existing Team
                    </button>
                  </div>
                  
                  {showExistingTeamDropdown && (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-64 overflow-y-auto p-2 bg-zinc-900 border border-zinc-700">
                      {existingTeams.map(team => (
                        <button
                          key={team.id}
                          onClick={() => handleSelectExistingTeam(team)}
                          className="flex items-center gap-2 p-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-left"
                          disabled={bracket.teams?.some(t => t.name === team.name)}
                        >
                          {team.logo_url ? (
                            <img src={team.logo_url} alt={team.name} className="w-8 h-8 object-contain" />
                          ) : (
                            <div 
                              className="w-8 h-8 rounded flex items-center justify-center text-xs font-bold"
                              style={{ backgroundColor: (team.primary_color || '#666') + '30', color: team.primary_color || '#666' }}
                            >
                              {team.abbreviation || team.name.charAt(0)}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <span className="text-xs text-white block truncate">{team.name}</span>
                            <span className="text-[10px] text-zinc-500">{team.sport}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Add Team Form */}
              <div className="space-y-3">
                <h4 className="text-sm font-bold text-zinc-400 uppercase">Add Team Manually</h4>
                <div className="grid grid-cols-1 md:grid-cols-7 gap-3 items-end">
                  <input
                    type="text"
                    value={newTeam.name}
                    onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
                    placeholder="Team Name"
                    className="bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
                  />
                  <input
                    type="text"
                    value={newTeam.abbreviation}
                    onChange={(e) => setNewTeam({ ...newTeam, abbreviation: e.target.value.toUpperCase() })}
                    placeholder="Abbrev (e.g. LAL)"
                    maxLength={5}
                    className="bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
                  />
                  <div className="space-y-1">
                    <input
                      type="text"
                      value={newTeam.logo_url}
                      onChange={(e) => setNewTeam({ ...newTeam, logo_url: e.target.value })}
                      placeholder="Logo URL"
                      className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
                    />
                  </div>
                  <input
                    type="text"
                    value={newTeam.website_url}
                    onChange={(e) => setNewTeam({ ...newTeam, website_url: e.target.value })}
                    placeholder="Website URL"
                    className="bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
                  />
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={newTeam.color}
                      onChange={(e) => setNewTeam({ ...newTeam, color: e.target.value })}
                      className="w-10 h-9 bg-zinc-800 border border-white/10 cursor-pointer"
                      title="Click to pick color or use eyedropper"
                    />
                    <input
                      type="text"
                      value={newTeam.color}
                      onChange={(e) => setNewTeam({ ...newTeam, color: e.target.value })}
                      className="flex-1 bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
                    />
                  </div>
                  {bracket.seeding_enabled && (
                    <input
                      type="number"
                      min="1"
                      max={bracket.num_teams}
                      value={newTeam.seed}
                      onChange={(e) => setNewTeam({ ...newTeam, seed: e.target.value })}
                      placeholder="Seed #"
                      className="bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm w-full"
                      data-testid="seed-input-new"
                    />
                  )}
                  <Button
                    onClick={onAddTeam}
                    disabled={!newTeam.name.trim()}
                    className="bg-zinc-700 text-white hover:bg-zinc-600 text-xs px-4 py-2 h-auto rounded-none disabled:opacity-50"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Team
                  </Button>
                </div>
                
                {/* Logo Preview with Auto-Extract */}
                {newTeam.logo_url && (
                  <div className="p-3 bg-zinc-900/50 border border-zinc-700 rounded space-y-2">
                    <div className="flex items-center gap-4">
                      <img 
                        src={newTeam.logo_url} 
                        alt="Logo preview" 
                        className="w-16 h-16 object-contain bg-white/5 p-1 rounded"
                        referrerPolicy="no-referrer"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                      <div className="flex-1 space-y-2">
                        <p className="text-xs text-white font-medium">Logo Preview</p>
                        <button
                          type="button"
                          onClick={handleExtractColor}
                          disabled={extractingColor}
                          className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-orange-600 hover:bg-orange-500 text-white text-xs font-bold rounded disabled:opacity-50"
                          data-testid="extract-bracket-color"
                        >
                          <Wand2 className={`w-3 h-3 ${extractingColor ? 'animate-spin' : ''}`} />
                          {extractingColor ? 'Extracting...' : 'Extract Color from Logo'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Teams Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {bracket.teams?.map((team) => (
                  editingTeam === team.id ? (
                    <div 
                      key={team.id} 
                      className="p-4 bg-zinc-800 border border-white/20 space-y-3"
                    >
                      <div className="grid grid-cols-3 gap-2">
                        <input
                          type="text"
                          value={editTeamData.name}
                          onChange={(e) => setEditTeamData({ ...editTeamData, name: e.target.value })}
                          placeholder="Team Name"
                          className="col-span-2 bg-zinc-900 border border-white/10 text-white px-2 py-1.5 text-sm"
                        />
                        <input
                          type="text"
                          value={editTeamData.abbreviation}
                          onChange={(e) => setEditTeamData({ ...editTeamData, abbreviation: e.target.value.toUpperCase() })}
                          placeholder="Abbrev"
                          maxLength={5}
                          className="bg-zinc-900 border border-white/10 text-white px-2 py-1.5 text-sm"
                        />
                      </div>
                      <input
                        type="text"
                        value={editTeamData.logo_url}
                        onChange={(e) => setEditTeamData({ ...editTeamData, logo_url: e.target.value })}
                        placeholder="Logo URL"
                        className="w-full bg-zinc-900 border border-white/10 text-white px-2 py-1.5 text-sm"
                      />
                      {/* Logo Preview for Edit */}
                      {editTeamData.logo_url && (
                        <div className="flex items-center gap-3 p-2 bg-zinc-900/50 border border-white/5">
                          <img 
                            src={editTeamData.logo_url} 
                            alt="Logo preview" 
                            className="w-12 h-12 object-contain"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                          <span className="text-[10px] text-zinc-500">Use eyedropper tool on this preview to pick team color →</span>
                        </div>
                      )}
                      <input
                        type="text"
                        value={editTeamData.website_url}
                        onChange={(e) => setEditTeamData({ ...editTeamData, website_url: e.target.value })}
                        placeholder="Website URL"
                        className="w-full bg-zinc-900 border border-white/10 text-white px-2 py-1.5 text-sm"
                      />
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={editTeamData.color}
                          onChange={(e) => setEditTeamData({ ...editTeamData, color: e.target.value })}
                          className="w-8 h-8 bg-zinc-900 border border-white/10 cursor-pointer"
                          title="Click to pick color"
                        />
                        <input
                          type="text"
                          value={editTeamData.color}
                          onChange={(e) => setEditTeamData({ ...editTeamData, color: e.target.value })}
                          className="flex-1 bg-zinc-900 border border-white/10 text-white px-2 py-1.5 text-sm"
                        />
                        {bracket.seeding_enabled && (
                          <input
                            type="number"
                            min="1"
                            max={bracket.num_teams}
                            value={editTeamData.seed}
                            onChange={(e) => setEditTeamData({ ...editTeamData, seed: e.target.value })}
                            placeholder="Seed"
                            className="w-16 bg-zinc-900 border border-white/10 text-white px-2 py-1.5 text-sm"
                          />
                        )}
                      </div>
                      <div className="flex items-center gap-2 pt-1">
                        <Button
                          onClick={() => onSaveTeam(team.id)}
                          className="flex-1 bg-green-600 text-white hover:bg-green-700 text-xs px-3 py-1.5 h-auto rounded-none"
                        >
                          <Save className="w-3 h-3 mr-1" /> Save
                        </Button>
                        <button onClick={() => setEditingTeam(null)} className="text-zinc-500 text-xs hover:text-white">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div 
                      key={team.id} 
                      className="flex items-center gap-4 p-4 bg-zinc-800/50 border border-white/5"
                      style={{ borderLeftColor: team.color, borderLeftWidth: 4 }}
                    >
                      {/* Seed Badge */}
                      {bracket.seeding_enabled && team.seed && (
                        <div className="w-8 h-8 rounded-full bg-yellow-500/20 border border-yellow-500/50 flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-bold text-yellow-500">{team.seed}</span>
                        </div>
                      )}
                      {team.logo_url ? (
                        <img src={team.logo_url} alt={team.name} className="w-10 h-10 object-contain" />
                      ) : (
                        <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold" style={{ backgroundColor: team.color + '30', color: team.color }}>
                          {team.abbreviation || team.name.charAt(0)}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-white block truncate font-medium">{team.name}</span>
                          {team.abbreviation && (
                            <span className="text-[10px] text-zinc-500 bg-zinc-800 px-1.5 py-0.5 rounded">{team.abbreviation}</span>
                          )}
                        </div>
                        {team.website_url && (
                          <a href={team.website_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-zinc-500 hover:text-blue-400 flex items-center gap-1">
                            <Globe className="w-3 h-3" /> Website
                          </a>
                        )}
                      </div>
                      <button onClick={() => onEditTeam(team)} className="text-zinc-500 hover:text-white">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button onClick={() => onDeleteTeam(team.id)} className="text-zinc-500 hover:text-red-500">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )
                ))}
              </div>

              {/* Teams count display */}
              {bracket.teams?.length > 0 && (
                <div className="mt-4 text-center text-zinc-500 text-xs">
                  {bracket.teams?.length} team(s) added to bracket
                </div>
              )}
            </div>
          )}

          {/* Bracket Tab */}
          {activeTab === "bracket" && (
            <div className="p-6 overflow-x-auto">
              <div className="min-w-[900px]">
                {/* Winners Bracket */}
                <div className="mb-8">
                  {bracket.bracket_type === "double" && (
                    <h4 className="font-secondary text-sm font-bold uppercase text-zinc-400 mb-4">Winners Bracket</h4>
                  )}
                  <div className="flex gap-8">
                    {[...new Set(bracket.games?.filter(g => !g.is_losers_bracket).map(g => g.round_number))].sort((a, b) => a - b).map(roundNum => (
                      <div key={roundNum} className="flex flex-col gap-4 min-w-[220px]">
                        <div className="text-xs font-bold uppercase text-zinc-500 text-center pb-2 border-b border-white/10">
                          {bracket.rounds?.find(r => r.round_number === roundNum)?.title || `Round ${roundNum}`}
                        </div>
                        {getRoundGames(roundNum, false).map(game => (
                          <GameCard
                            key={game.id}
                            game={game}
                            bracket={bracket}
                            broadcasts={broadcasts}
                            getTeamById={getTeamById}
                            isEditing={editingGame === game.id}
                            formData={gameFormData}
                            setFormData={setGameFormData}
                            onEdit={() => handleEditGame(game)}
                            onSave={() => handleSaveGame(game.id)}
                            onCancel={() => setEditingGame(null)}
                            onAdvance={() => onAdvanceWinner(game.id)}
                            eventColor={eventColor}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Losers Bracket */}
                {bracket.bracket_type === "double" && losersRounds.length > 0 && (
                  <div className="pt-6 border-t border-white/10">
                    <h4 className="font-secondary text-sm font-bold uppercase text-red-400 mb-4">Losers Bracket</h4>
                    <div className="flex gap-8">
                      {[...new Set(bracket.games?.filter(g => g.is_losers_bracket).map(g => g.round_number))].sort((a, b) => a - b).map(roundNum => (
                        <div key={roundNum} className="flex flex-col gap-4 min-w-[220px]">
                          <div className="text-xs font-bold uppercase text-zinc-500 text-center pb-2 border-b border-white/10">
                            {bracket.rounds?.find(r => r.round_number === roundNum)?.title || `Round ${roundNum}`}
                          </div>
                          {getRoundGames(roundNum, true).map(game => (
                            <GameCard
                              key={game.id}
                              game={game}
                              bracket={bracket}
                              broadcasts={broadcasts}
                              getTeamById={getTeamById}
                              isEditing={editingGame === game.id}
                              formData={gameFormData}
                              setFormData={setGameFormData}
                              onEdit={() => handleEditGame(game)}
                              onSave={() => handleSaveGame(game.id)}
                              onCancel={() => setEditingGame(null)}
                              onAdvance={() => onAdvanceWinner(game.id)}
                              eventColor={eventColor}
                              isLosers
                            />
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Rounds Tab */}
          {activeTab === "rounds" && (
            <div className="p-6 space-y-3">
              {bracket.rounds?.map(round => (
                <div key={round.round_number} className="flex items-center gap-4 p-4 bg-zinc-800/50 border border-white/5">
                  <div className="w-10 h-10 flex items-center justify-center bg-zinc-700 text-white font-bold">
                    {round.round_number}
                  </div>
                  
                  {editingRound === round.round_number ? (
                    <>
                      <input
                        type="text"
                        value={roundTitle}
                        onChange={(e) => setRoundTitle(e.target.value)}
                        placeholder="Round Title"
                        className="flex-1 bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
                      />
                      <input
                        type="text"
                        value={roundLocation}
                        onChange={(e) => setRoundLocation(e.target.value)}
                        placeholder="Location (optional)"
                        className="flex-1 bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
                      />
                      <Button
                        onClick={() => {
                          onUpdateRound(round.round_number, roundTitle, roundLocation);
                          setEditingRound(null);
                        }}
                        className="bg-green-600 text-white hover:bg-green-700 text-xs px-4 py-2 h-auto rounded-none"
                      >
                        <Save className="w-4 h-4" />
                      </Button>
                      <button onClick={() => setEditingRound(null)} className="text-zinc-500 hover:text-white">
                        <X className="w-5 h-5" />
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="flex-1">
                        <p className="text-white font-medium">{round.title || `Round ${round.round_number}`}</p>
                        {round.location && (
                          <p className="text-xs text-zinc-500 flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {round.location}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          setEditingRound(round.round_number);
                          setRoundTitle(round.title || "");
                          setRoundLocation(round.location || "");
                        }}
                        className="text-zinc-500 hover:text-white"
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Game Card Component for Admin
const GameCard = ({ 
  game, bracket, broadcasts, getTeamById, isEditing, formData, setFormData,
  onEdit, onSave, onCancel, onAdvance, eventColor, isLosers = false
}) => {
  const team1 = getTeamById(game.team1_id);
  const team2 = getTeamById(game.team2_id);

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  if (isEditing) {
    return (
      <div className="p-4 bg-zinc-800 border border-white/20 space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-[10px] text-zinc-500 uppercase mb-1">Team 1</label>
            <select
              value={formData.team1_id}
              onChange={(e) => setFormData({ ...formData, team1_id: e.target.value })}
              className="w-full bg-zinc-900 border border-white/10 text-white px-2 py-1.5 text-xs"
            >
              <option value="">-- Select --</option>
              {bracket.teams?.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] text-zinc-500 uppercase mb-1">Team 2</label>
            <select
              value={formData.team2_id}
              onChange={(e) => setFormData({ ...formData, team2_id: e.target.value })}
              className="w-full bg-zinc-900 border border-white/10 text-white px-2 py-1.5 text-xs"
            >
              <option value="">-- Select --</option>
              {bracket.teams?.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-[10px] text-zinc-500 uppercase mb-1">Score</label>
            <input
              type="number"
              value={formData.team1_score}
              onChange={(e) => setFormData({ ...formData, team1_score: e.target.value ? parseInt(e.target.value) : "" })}
              className="w-full bg-zinc-900 border border-white/10 text-white px-2 py-1.5 text-xs"
            />
          </div>
          <div>
            <label className="block text-[10px] text-zinc-500 uppercase mb-1">Score</label>
            <input
              type="number"
              value={formData.team2_score}
              onChange={(e) => setFormData({ ...formData, team2_score: e.target.value ? parseInt(e.target.value) : "" })}
              className="w-full bg-zinc-900 border border-white/10 text-white px-2 py-1.5 text-xs"
            />
          </div>
        </div>

        {formData.team1_id && formData.team2_id && (
          <div>
            <label className="block text-[10px] text-zinc-500 uppercase mb-1">Winner</label>
            <select
              value={formData.winner_id}
              onChange={(e) => setFormData({ ...formData, winner_id: e.target.value })}
              className="w-full bg-zinc-900 border border-white/10 text-white px-2 py-1.5 text-xs"
            >
              <option value="">-- Select Winner --</option>
              {formData.team1_id && <option value={formData.team1_id}>{getTeamById(formData.team1_id)?.name}</option>}
              {formData.team2_id && <option value={formData.team2_id}>{getTeamById(formData.team2_id)?.name}</option>}
            </select>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-[10px] text-zinc-500 uppercase mb-1">Date</label>
            <input
              type="date"
              value={formData.game_date}
              onChange={(e) => setFormData({ ...formData, game_date: e.target.value })}
              className="w-full bg-zinc-900 border border-white/10 text-white px-2 py-1.5 text-xs"
            />
          </div>
          <div>
            <label className="block text-[10px] text-zinc-500 uppercase mb-1">Time</label>
            <input
              type="time"
              value={formData.game_time}
              onChange={(e) => setFormData({ ...formData, game_time: e.target.value })}
              className="w-full bg-zinc-900 border border-white/10 text-white px-2 py-1.5 text-xs"
            />
          </div>
        </div>

        <div>
          <label className="block text-[10px] text-zinc-500 uppercase mb-1">Broadcast</label>
          <select
            value={formData.broadcast_id}
            onChange={(e) => setFormData({ ...formData, broadcast_id: e.target.value })}
            className="w-full bg-zinc-900 border border-white/10 text-white px-2 py-1.5 text-xs"
          >
            <option value="">-- Link Broadcast --</option>
            {broadcasts?.map(b => <option key={b.id} value={b.id}>{b.title} ({b.scheduled_date})</option>)}
          </select>
        </div>

        <div>
          <label className="block text-[10px] text-zinc-500 uppercase mb-1">
            <BarChart2 className="w-3 h-3 inline mr-1" />
            Stats Link (StatMoose/PrestoSports)
          </label>
          <input
            type="url"
            value={formData.stats_link}
            onChange={(e) => setFormData({ ...formData, stats_link: e.target.value })}
            placeholder="https://statmoose.com/... or https://prestosports.com/..."
            className="w-full bg-zinc-900 border border-white/10 text-white px-2 py-1.5 text-xs"
            data-testid="game-stats-link"
          />
          <p className="text-[9px] text-zinc-600 mt-0.5">Used for box scores and stat leaders</p>
        </div>

        <div className="flex items-center gap-2 pt-2">
          <Button onClick={onSave} className="flex-1 bg-green-600 text-white hover:bg-green-700 text-xs px-3 py-1.5 h-auto rounded-none">
            Save
          </Button>
          <button onClick={onCancel} className="text-zinc-500 text-xs hover:text-white">Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`p-3 ${game.winner_id ? 'bg-zinc-800/80' : 'bg-zinc-900/50'} rounded shadow cursor-pointer hover:bg-zinc-800`}
      onClick={onEdit}
    >
      <div className={`flex items-center gap-2 py-1.5 ${game.winner_id === game.team1_id ? 'bg-green-500/10 rounded px-2' : ''}`}>
        {team1 ? (
          <>
            {team1.logo_url ? (
              <img src={team1.logo_url} alt={team1.name} className="w-5 h-5 object-contain" />
            ) : (
              <div className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold" style={{ backgroundColor: team1.color + '30', color: team1.color }}>
                {team1.name.charAt(0)}
              </div>
            )}
            <span className="text-xs text-white flex-1 truncate">{team1.name}</span>
            {game.team1_score !== null && (
              <span className={`text-sm font-bold ${game.winner_id === game.team1_id ? 'text-green-500' : 'text-white'}`}>
                {game.team1_score}
              </span>
            )}
          </>
        ) : (
          <span className="text-xs text-zinc-600 italic">TBD</span>
        )}
      </div>

      <div className="border-t border-white/5 my-1"></div>

      <div className={`flex items-center gap-2 py-1.5 ${game.winner_id === game.team2_id ? 'bg-green-500/10 rounded px-2' : ''}`}>
        {team2 ? (
          <>
            {team2.logo_url ? (
              <img src={team2.logo_url} alt={team2.name} className="w-5 h-5 object-contain" />
            ) : (
              <div className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold" style={{ backgroundColor: team2.color + '30', color: team2.color }}>
                {team2.name.charAt(0)}
              </div>
            )}
            <span className="text-xs text-white flex-1 truncate">{team2.name}</span>
            {game.team2_score !== null && (
              <span className={`text-sm font-bold ${game.winner_id === game.team2_id ? 'text-green-500' : 'text-white'}`}>
                {game.team2_score}
              </span>
            )}
          </>
        ) : (
          <span className="text-xs text-zinc-600 italic">TBD</span>
        )}
      </div>

      {(game.game_date || game.game_time || game.stats_link) && (
        <div className="mt-2 pt-2 border-t border-white/5 text-[10px] text-zinc-500 flex items-center gap-2">
          {game.game_date && (
            <>
              <Calendar className="w-3 h-3" />
              <span>{game.game_date}</span>
            </>
          )}
          {game.game_time && <span>{formatTime(game.game_time)}</span>}
          {game.stats_link && (
            <span className="flex items-center gap-1 text-green-500 ml-auto" title="Stats link configured">
              <BarChart2 className="w-3 h-3" /> Stats
            </span>
          )}
        </div>
      )}

      {game.winner_id && game.next_game_id && (
        <button
          onClick={(e) => { e.stopPropagation(); onAdvance(); }}
          className="w-full mt-2 text-[10px] py-1 bg-white/10 text-zinc-400 hover:text-white hover:bg-white/20 rounded"
        >
          Advance Winner →
        </button>
      )}
    </div>
  );
};

export default BracketManagerPage;
